/**
 * EntityPicker — single-select combobox with search, paging and keyboard control.
 *
 * WHY THIS EXISTS
 * ---------------
 * CustomerPicker was written for one job and a second surface now needs the same widget for
 * escalation policies. The two options were to copy it and adjust the copy, or to lift the
 * mechanics out and let both callers supply their own vocabulary. Copying would have produced two
 * ~190-line files that drift the first time a keyboard bug is fixed in one of them — the fork that
 * Architecture Principle 8 exists to prevent.
 *
 * So the mechanics live here, once, and CustomerPicker is now a thin wrapper over it with the same
 * exported name, the same props and the same behaviour, so no existing call site changes.
 *
 * WHY NOT A NATIVE <select>
 * -------------------------
 * Unsearchable past a handful of options, renders an OS-drawn list no design system can style, and
 * swallows keyboard events differently from the surrounding form.
 *
 * COPY IS THE CALLER'S
 * --------------------
 * Every user-visible string is a prop. A generic component that writes its own sentences would
 * either say "item" — which means nothing to an operator — or would grow a switch over what kind
 * of thing it is holding, which is the fork again, wearing a different hat.
 *
 * Behaviour: search filters on the caller's search text; results page at 10 with prev/next, and the
 * paging controls are absent entirely at 10 or fewer so a small list shows no chrome it doesn't
 * need. Escape closes, arrows move, Enter selects, click-outside dismisses.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Check, ChevronDown } from 'lucide-react';
import { Pill } from './Pill';
import { PickerPagingFooter } from './PickerPagingFooter';
import { TabNav } from './TabNav';

const PAGE_SIZE = 10;
const ALL_GROUP = '__all__';

export function EntityPicker({
  items = [],
  value,
  onChange,
  loading = false,
  disabled = false,
  ariaLabel = 'Select an option',
  icon = null,

  // ── how to read an item ──
  getId = (x) => x?.id,
  getLabel = (x) => x?.name ?? x?.id,
  /** Second line under the label. Return null for none. */
  getSublabel = () => null,
  /** Short marker rendered as a pill next to the label. Return null for none. */
  getBadge = () => null,
  /** Text the search box matches against. Defaults to label + id. */
  getSearchText = null,
  /** Sort key. Defaults to the label, i.e. alphabetical. Return a string. */
  getSortKey = null,

  // ── optional category tabs (additive; omit for the plain combobox) ──
  /** [{ id, label }] — when provided, a tab row is shown (plus an "All" tab) and
   *  the list is filtered to the active tab. */
  groups = null,
  /** item -> group id, required when `groups` is set. */
  getGroupId = null,
  allTabLabel = 'All',

  // ── copy ──
  placeholder = 'Choose an option',
  loadingText = 'Loading…',
  searchPlaceholder = 'Search',
  /** (query, total) => string. Shown when the filtered list is empty. */
  emptyMessage = (q, total) => (q
    ? `Nothing matches “${q}”. Try fewer characters, or clear the search to see all ${total}.`
    : 'There is nothing to choose from yet.'),

  // ── the explicit "none" option ──
  /**
   * When true a first row is offered that clears the selection, calling onChange(null).
   * Offered as a real row rather than an X on the trigger because "no policy" is a deliberate,
   * meaningful choice here, not the absence of one — and it deserves a sentence explaining it.
   */
  allowClear = false,
  clearLabel = 'None',
  clearSublabel = null,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [cursor, setCursor] = useState(0);
  const [activeGroup, setActiveGroup] = useState(ALL_GROUP);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const hasTabs = Array.isArray(groups) && groups.length > 0 && typeof getGroupId === 'function';

  const selected = useMemo(
    () => items.find((x) => getId(x) === value) || null,
    [items, value, getId],
  );

  const searchText = getSearchText
    || ((x) => [getLabel(x), getId(x)].filter(Boolean).join(' '));
  const sortKey = getSortKey || ((x) => String(getLabel(x) ?? ''));

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = items.slice();
    if (hasTabs && activeGroup !== ALL_GROUP) {
      list = list.filter((x) => getGroupId(x) === activeGroup);
    }
    list.sort((a, b) => String(sortKey(a)).localeCompare(String(sortKey(b))));
    if (!q) return list;
    return list.filter((x) => String(searchText(x)).toLowerCase().includes(q));
  }, [items, query, sortKey, searchText, hasTabs, activeGroup, getGroupId]);

  // Per-tab counts (respecting the current search), shown as tab badges.
  const groupCounts = useMemo(() => {
    if (!hasTabs) return {};
    const q = query.trim().toLowerCase();
    const counts = { [ALL_GROUP]: 0 };
    items.forEach((x) => {
      if (q && !String(searchText(x)).toLowerCase().includes(q)) return;
      counts[ALL_GROUP] += 1;
      const g = getGroupId(x);
      counts[g] = (counts[g] || 0) + 1;
    });
    return counts;
  }, [items, hasTabs, getGroupId, query, searchText]);

  // The clear row is prepended AFTER filtering and is never paged away, so "set this back to
  // none" is always one click from wherever the operator is in a long list.
  const CLEAR = useMemo(() => ({ __clear: true }), []);

  const pageCount = Math.max(1, Math.ceil(matches.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageItems = matches.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const visible = allowClear ? [CLEAR, ...pageItems] : pageItems;

  // A new search (or tab switch) should start at the first page of its own results.
  useEffect(() => { setPage(0); setCursor(0); }, [query, activeGroup]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const choose = (x) => {
    if (!x) return;
    onChange?.(x.__clear ? null : getId(x));
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((i) => Math.min(i + 1, visible.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') { e.preventDefault(); choose(visible[cursor]); }
  };

  const triggerText = loading
    ? loadingText
    : (selected ? getLabel(selected) : (allowClear && value == null ? clearLabel : placeholder));

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-v2-md border
          border-v2-border bg-v2-surface text-left text-[13px] text-v2-ink
          hover:border-v2-border-strong focus:outline-none focus:border-v2-action focus:shadow-v2-xs
          ${disabled || loading ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <span className="flex items-center gap-2 min-w-0">
          {icon}
          <span className="truncate">{triggerText}</span>
        </span>
        <ChevronDown size={14} className="text-v2-ink-muted shrink-0" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={ariaLabel}
          onKeyDown={onKeyDown}
          // Keep clicks inside the popover from bubbling to an ancestor's
          // click-outside handler (e.g. a Slideout's dismiss-on-outside-mousedown),
          // which would otherwise close the surrounding panel when picking an option.
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute z-30 mt-1 w-full rounded-v2-md border border-v2-border bg-v2-surface shadow-v2-md overflow-hidden"
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-v2-border">
            <Search size={14} className="text-v2-ink-muted shrink-0" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full bg-transparent text-[13px] text-v2-ink placeholder:text-v2-ink-muted focus:outline-none"
            />
          </div>

          {hasTabs && (
            <div className="overflow-x-auto">
              <TabNav
                ariaLabel="Framework categories"
                activeId={activeGroup}
                onChange={setActiveGroup}
                className="px-1"
                tabs={[{ id: ALL_GROUP, label: allTabLabel, badge: (
                  <span className="text-[10px] text-v2-ink-muted">{groupCounts[ALL_GROUP] || 0}</span>
                ) }].concat(groups.map((g) => ({
                  id: g.id,
                  label: g.label,
                  badge: <span className="text-[10px] text-v2-ink-muted">{groupCounts[g.id] || 0}</span>,
                })))}
              />
            </div>
          )}

          {visible.length === 0 ? (
            <div className="px-3 py-4 text-[12.5px] text-v2-ink-muted">
              {emptyMessage(query.trim(), items.length)}
            </div>
          ) : (
            <ul className="max-h-72 overflow-auto py-1">
              {visible.map((x, i) => {
                const isClear = !!x.__clear;
                const id = isClear ? '__clear' : getId(x);
                const isSelected = isClear ? value == null : id === value;
                const sub = isClear ? clearSublabel : getSublabel(x);
                const badge = isClear ? null : getBadge(x);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onMouseEnter={() => setCursor(i)}
                      onClick={() => choose(x)}
                      className={`w-full flex items-start justify-between gap-2 px-3 py-2 text-left text-[13px]
                        ${i === cursor ? 'bg-v2-bg-alt' : ''} hover:bg-v2-bg-alt`}
                    >
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="truncate">{isClear ? clearLabel : getLabel(x)}</span>
                          {badge && <Pill tone="brand" size="sm">{badge}</Pill>}
                        </span>
                        {sub && <span className="block text-[12px] text-v2-ink-muted mt-0.5">{sub}</span>}
                      </span>
                      {isSelected && <Check size={14} className="text-v2-action shrink-0 mt-0.5" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Paging appears only when it is needed — a short list shows no controls at all. The
              strip itself is shared with ApproverPicker, which pages the same way over a directory
              it fetches rather than a list it holds. Two copies of this markup would have drifted
              the first time either grew a fix. */}
          <PickerPagingFooter
            page={safePage}
            pageCount={pageCount}
            total={matches.length}
            pageSize={PAGE_SIZE}
            onPage={setPage}
            noun="options"
          />
        </div>
      )}
    </div>
  );
}

export default EntityPicker;
